import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type DoctorLabOrder } from '../api';

/**
 * Lab tests a doctor ordered for this patient.
 *
 * `GET /lab/orders` has been serving the real requests doctors raise and send
 * from their own panel, and nothing on the web read it — so a patient told
 * "get these done before your next visit" left the consultation with no
 * record of it anywhere in the app. On this account it is a lipid profile and
 * an HbA1c, with the doctor's own note that the sample must be fasting.
 *
 * The order is the doctor's; the app does not act on it. What it can do is
 * carry the test name into the booking flow, which is what "Find a lab" does.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 14, boxShadow: T.shadow, fontFamily: T.font,
};

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function DoctorLabOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<DoctorLabOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setOrders(await patientApi.doctorLabOrders() ?? []);
    } catch {
      // This is a section on someone else's page; it stays away rather than
      // taking the page down with it.
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading || orders.length === 0) return null;

  return (
    <div style={{ ...card, borderColor: `${T.teal}55`, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Tests your doctor ordered</div>
      <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3, lineHeight: 1.5 }}>
        Raised in the doctor's own panel. Booking one is up to you — the order does not book itself.
      </div>

      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {orders.map((o) => (
          <div key={o.id} style={{ background: T.ice, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{o.doctorName || 'Your doctor'}</div>
                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>
                  {[fmtDate(o.orderedOn), o.priority && o.priority.toLowerCase() !== 'routine' ? o.priority.toUpperCase() : null]
                    .filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {o.tests.map((t) => (
                <div key={t.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: T.textSec, marginTop: 2 }}>
                      {[t.category, t.sampleType, t.fasting ? 'Fasting sample' : null, t.reason]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/patient/care-services/labs/compare?test=${encodeURIComponent(t.name)}`)}
                    style={{
                      padding: '7px 12px', borderRadius: 9, border: `1px solid ${T.teal}`,
                      background: T.white, cursor: 'pointer', fontSize: 10.5, fontWeight: 700,
                      color: T.teal, fontFamily: T.font, whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    Find a lab
                  </button>
                </div>
              ))}
            </div>

            {o.clinicalNotes && (
              <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 10, lineHeight: 1.5, fontStyle: 'italic' }}>
                “{o.clinicalNotes}”
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
